"use client";

import { useEffect, useRef, useState, type RefObject } from "react";
import Link from "next/link";
import { generateKundali } from "@/lib/api/kundali";
import type { KundaliRequest, KundaliResponse } from "@/lib/api/kundali";
import BirthDetailsForm from "@/components/BirthDetailsForm";
import CalculatorCrossLinks from "@/components/CalculatorCrossLinks";
import { trackCalculatorUsed } from "@/lib/analytics";
import RatingPrompt from "@/components/RatingPrompt";
import { RESULT_REASONS } from "@/lib/feedback/resultReasons";

const RASHI_INFO: Record<
  string,
  { hi: string; symbol: string; ruler: string; element: string; quality: string }
> = {
  aries: { hi: "मेष", symbol: "♈", ruler: "Mars", element: "Fire", quality: "Cardinal" },
  taurus: { hi: "वृषभ", symbol: "♉", ruler: "Venus", element: "Earth", quality: "Fixed" },
  gemini: { hi: "मिथुन", symbol: "♊", ruler: "Mercury", element: "Air", quality: "Mutable" },
  cancer: { hi: "कर्क", symbol: "♋", ruler: "Moon", element: "Water", quality: "Cardinal" },
  leo: { hi: "सिंह", symbol: "♌", ruler: "Sun", element: "Fire", quality: "Fixed" },
  virgo: { hi: "कन्या", symbol: "♍", ruler: "Mercury", element: "Earth", quality: "Mutable" },
  libra: { hi: "तुला", symbol: "♎", ruler: "Venus", element: "Air", quality: "Cardinal" },
  scorpio: { hi: "वृश्चिक", symbol: "♏", ruler: "Mars", element: "Water", quality: "Fixed" },
  sagittarius: { hi: "धनु", symbol: "♐", ruler: "Jupiter", element: "Fire", quality: "Mutable" },
  capricorn: { hi: "मकर", symbol: "♑", ruler: "Saturn", element: "Earth", quality: "Cardinal" },
  aquarius: { hi: "कुंभ", symbol: "♒", ruler: "Saturn", element: "Air", quality: "Fixed" },
  pisces: { hi: "मीन", symbol: "♓", ruler: "Jupiter", element: "Water", quality: "Mutable" },
};

type RashiInfo = (typeof RASHI_INFO)[string];

export default function AscendantCalculatorClient() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<KundaliResponse | null>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  // Scroll the result into view after submit — the form disappears and the
  // result would otherwise render at the top, out of sight on long pages.
  useEffect(() => {
    if (result && resultRef.current) {
      resultRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [result]);

  async function handleSubmit(data: KundaliRequest) {
    setLoading(true);
    setError(null);
    try {
      const response = await generateKundali({ ...data, skip_interpretation: true });
      setResult(response);
      trackCalculatorUsed({ calculator: "ascendant-calculator" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setLoading(false);
    }
  }

  const lagnaKey = result?.lagna_rashi?.toLowerCase();
  const lagnaInfo = lagnaKey ? RASHI_INFO[lagnaKey] : null;

  return (
    <div className="sacred-bg min-h-[calc(100vh-130px)]">
      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-12 sm:py-16">
        <AscendantHeader />

        {/* Form */}
        {!result && (
          <div className="mx-auto max-w-md">
            <BirthDetailsForm
              onSubmit={handleSubmit}
              loading={loading}
              submitLabel="Find My Ascendant"
              error={error ?? undefined}
              requireBirthTime
              analyticsToolName="ascendant-calculator"
            />
          </div>
        )}

        {result && lagnaInfo && (
          <AscendantResults
            result={result}
            lagnaInfo={lagnaInfo}
            resultRef={resultRef}
            onReset={() => {
              setResult(null);
              setError(null);
            }}
          />
        )}

        {result && lagnaInfo && (
          <div className="mt-6">
            <RatingPrompt
              source={"tool_result" as never}
              mode="thumbs+reasons"
              reasons={RESULT_REASONS}
              prompt="Was this lagna result accurate?"
              context={{
                surface: "ascendant_calculator_result",
                path: typeof window !== "undefined" ? window.location.pathname : null,
              }}
            />
          </div>
        )}

        <AscendantSeoContent />

        {/* Cross Links */}
        <CalculatorCrossLinks currentPage="ascendant-calculator" />
      </div>
    </div>
  );
}

function AscendantHeader() {
  return (
    <div className="text-center mb-10">
      <p
        className="text-[11px] font-semibold uppercase tracking-[0.25em] mb-3"
        style={{ color: "var(--ish-accent)" }}
      >
        Lagna Ganana
      </p>
      <h1
        className="text-[28px] sm:text-[36px] font-bold leading-tight"
        style={{
          fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
          color: "var(--ish-dark)",
        }}
      >
        Free Lagna (Ascendant) Calculator
      </h1>
      <p className="mt-3 text-[15px] leading-relaxed max-w-md mx-auto text-ish-text-muted">
        Enter your birth date, time, and place to find your Lagna (rising sign). Get your Ascendant
        Rashi, Lagna lord, and personality traits — 100% free.
      </p>

      <div
        className="mt-5 flex flex-wrap justify-center gap-x-5 gap-y-2 text-[11px] font-medium"
        style={{ color: "var(--ish-text-faint)" }}
      >
        <TrustSignal icon="shield" label="Accurate Vedic calculations" />
        <TrustSignal icon="clock" label="Instant Results" />
        <TrustSignal icon="user" label="100% Free" />
      </div>

      <div
        className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12px]"
        style={{
          backgroundColor: "var(--ish-accent-light)",
          color: "var(--ish-accent)",
          border: "1px solid var(--ish-accent-border)",
        }}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M12 8v4M12 16h.01" />
        </svg>
        Birth time is crucial for accurate ascendant calculation
      </div>
    </div>
  );
}

function TrustSignal({ icon, label }: { icon: "shield" | "clock" | "user"; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        {icon === "shield" && <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />}
        {icon === "clock" && (
          <>
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </>
        )}
        {icon === "user" && (
          <>
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </>
        )}
      </svg>
      {label}
    </span>
  );
}

function AscendantResults({
  result,
  lagnaInfo,
  resultRef,
  onReset,
}: {
  result: KundaliResponse;
  lagnaInfo: RashiInfo;
  resultRef: RefObject<HTMLDivElement | null>;
  onReset: () => void;
}) {
  return (
    <div ref={resultRef} className="space-y-6">
      <div className="card-sacred p-6 sm:p-8 text-center">
        <span className="text-5xl block mb-3">{lagnaInfo.symbol}</span>
        <h2
          className="text-[28px] font-bold"
          style={{
            fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
            color: "var(--ish-dark)",
          }}
        >
          {result.lagna_rashi} Ascendant
        </h2>
        <p className="text-[18px] mt-1 text-ish-text-muted">{lagnaInfo.hi} लग्न</p>

        <div className="divider-warm my-5" />

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <ResultFact label="Lagna Lord" value={lagnaInfo.ruler} />
          <ResultFact label="Element" value={lagnaInfo.element} />
          <ResultFact label="Quality" value={lagnaInfo.quality} />
          <ResultFact label="Moon Sign" value={result.moon_rashi} className="capitalize" />
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Link
          href="/free-kundali"
          className="btn-sacred text-center font-semibold py-3 px-6 rounded-lg text-[14px]"
        >
          Get Full Kundali Reading &rarr;
        </Link>
        <button
          onClick={onReset}
          className="text-center font-semibold py-3 px-6 rounded-lg text-[14px] transition-all duration-200"
          style={{
            color: "var(--ish-text-muted)",
            border: "1px solid var(--ish-border)",
          }}
        >
          Calculate Again
        </button>
      </div>
    </div>
  );
}

function ResultFact({
  label,
  value,
  className = "",
}: {
  label: string;
  value: string;
  className?: string;
}) {
  return (
    <div>
      <span className="block text-[12px] mb-1 text-ish-text-faint">{label}</span>
      <span className={`font-semibold text-[14px] text-ish-dark ${className}`}>{value}</span>
    </div>
  );
}

function AscendantSeoContent() {
  return (
    <div className="mt-12 card-sacred p-6 sm:p-8">
      <h2
        className="text-[20px] font-bold mb-4"
        style={{
          fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
          color: "var(--ish-dark)",
        }}
      >
        What is the Ascendant (Lagna)?
      </h2>
      <div className="space-y-3 text-[14px] leading-relaxed text-ish-text-muted">
        <p>
          The <strong style={{ color: "var(--ish-dark)" }}>Ascendant</strong> (Lagna in Sanskrit) is
          the zodiac sign that was rising on the eastern horizon at the exact moment and location of
          your birth. It is the most time-sensitive point in your birth chart — it changes
          approximately every 2 hours, which is why knowing your precise birth time is essential.
        </p>
        <p>
          In Vedic astrology, the Ascendant is considered even more important than the Sun or Moon
          sign. It determines the layout of all 12 houses in your chart and influences your physical
          appearance, health, personality, and how the world perceives you. The lord of the
          Ascendant sign (Lagnesh) becomes the most important planet in your horoscope.
        </p>
        <p>
          While your Moon sign (Rashi) reflects your emotional inner world, your Ascendant reflects
          your outward personality, physical constitution, and the lens through which you experience
          life. A complete Vedic reading considers both alongside the Sun sign for a holistic
          understanding.
        </p>
      </div>
    </div>
  );
}
